/**
 * Message Queue Service for Optimistic Updates
 *
 * Manages pending messages with localStorage persistence, retry logic,
 * and background synchronization.
 */

import {
  MESSAGE_QUEUE_CONFIG,
  type PendingMessage,
  type PendingAttachment,
  type PersistedPendingMessages,
  type PersistedPendingMessage,
  type MessageStatus,
  type ChatType,
  type OptimisticMessageInput,
} from '@/types/pendingMessage';

/**
 * Generate a unique client ID for a pending message
 */
export function generateClientId(): string {
  // Use native crypto.randomUUID() which is available in all modern browsers
  return crypto.randomUUID();
}

/**
 * Create pending attachments from files
 */
export function createPendingAttachments(files: File[]): PendingAttachment[] {
  return files.map((file) => ({
    id: generateClientId(),
    file,
    uploadProgress: 0,
    uploaded: undefined,
    error: undefined,
  }));
}

/**
 * Create a new pending message
 */
export function createPendingMessage(
  input: OptimisticMessageInput,
  chatType: ChatType,
  userId: string,
  author: string,
  authorRole?: string,
  conversationId?: string
): PendingMessage {
  const now = Date.now();
  return {
    clientId: generateClientId(),
    status: 'pending',
    retryCount: 0,
    createdAt: now,
    statusChangedAt: now,
    text: input.text,
    pendingAttachments: input.files ? createPendingAttachments(input.files) : undefined,
    uploadedAttachments: input.uploadedAttachments,
    quotedMessageId: input.quotedMessageId,
    quotedMessage: input.quotedMessage,
    mentions: input.mentions,
    mentionedUserIds: input.mentionedUserIds,
    chatType,
    conversationId,
    userId,
    author,
    authorRole,
  };
}

/**
 * Load persisted pending messages from localStorage
 */
export function loadPersistedMessages(): PendingMessage[] {
  try {
    const stored = localStorage.getItem(MESSAGE_QUEUE_CONFIG.STORAGE_KEY);
    if (!stored) return [];

    const data: PersistedPendingMessages = JSON.parse(stored);

    // Check if data is stale
    if (Date.now() - data.timestamp > MESSAGE_QUEUE_CONFIG.STALE_THRESHOLD_MS) {
      localStorage.removeItem(MESSAGE_QUEUE_CONFIG.STORAGE_KEY);
      return [];
    }

    // Convert persisted messages back to PendingMessage format
    return data.messages.map((persisted) => ({
      clientId: persisted.clientId,
      status: persisted.hasUnsentAttachments ? 'failed' : ('pending' as MessageStatus),
      retryCount: 0,
      createdAt: persisted.createdAt,
      text: persisted.text,
      quotedMessageId: persisted.quotedMessageId,
      quotedMessage: persisted.quotedMessage,
      chatType: persisted.chatType,
      conversationId: persisted.conversationId,
      userId: persisted.userId,
      author: persisted.author,
      authorRole: persisted.authorRole,
      // Mark as having lost attachments if they were pending
      error: persisted.hasUnsentAttachments
        ? 'Attachments were lost. Tap to retry without attachments.'
        : undefined,
      // No pendingAttachments - files can't be restored from localStorage
    }));
  } catch (error) {
    console.error('Error loading persisted messages:', error);
    localStorage.removeItem(MESSAGE_QUEUE_CONFIG.STORAGE_KEY);
    return [];
  }
}

/**
 * Persist pending messages to localStorage
 * Note: File objects cannot be serialized, so we track if attachments were pending
 */
export function persistMessages(messages: PendingMessage[]): void {
  try {
    // Only persist messages that haven't been sent yet
    const unsent = messages.filter((m) => m.status !== 'sent');

    if (unsent.length === 0) {
      localStorage.removeItem(MESSAGE_QUEUE_CONFIG.STORAGE_KEY);
      return;
    }

    const persisted: PersistedPendingMessages = {
      messages: unsent.map(
        (m): PersistedPendingMessage => ({
          clientId: m.clientId,
          chatType: m.chatType,
          conversationId: m.conversationId,
          text: m.text,
          quotedMessageId: m.quotedMessageId,
          quotedMessage: m.quotedMessage,
          hasUnsentAttachments: Boolean(m.pendingAttachments?.some((a) => !a.uploaded && a.file)),
          createdAt: m.createdAt,
          userId: m.userId,
          author: m.author,
          authorRole: m.authorRole,
        })
      ),
      timestamp: Date.now(),
    };

    localStorage.setItem(MESSAGE_QUEUE_CONFIG.STORAGE_KEY, JSON.stringify(persisted));
  } catch (error) {
    console.error('Error persisting messages:', error);
  }
}

/**
 * Calculate retry delay with exponential backoff
 */
export function getRetryDelay(retryCount: number): number {
  return MESSAGE_QUEUE_CONFIG.RETRY_BASE_DELAY * Math.pow(2, retryCount);
}

/**
 * Check if a message should be retried
 */
export function shouldRetry(message: PendingMessage): boolean {
  return message.retryCount < MESSAGE_QUEUE_CONFIG.MAX_RETRIES;
}

/**
 * Check if the browser is online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

/**
 * Update a message's status in the queue
 */
export function updateMessageStatus(
  messages: PendingMessage[],
  clientId: string,
  updates: Partial<PendingMessage>
): PendingMessage[] {
  return messages.map((m) =>
    m.clientId === clientId ? { ...m, ...updates, statusChangedAt: Date.now() } : m
  );
}

/**
 * Update an attachment's progress in a message
 */
export function updateAttachmentProgress(
  messages: PendingMessage[],
  clientId: string,
  attachmentId: string,
  progress: number
): PendingMessage[] {
  return messages.map((m) => {
    if (m.clientId !== clientId) return m;

    const updatedAttachments = m.pendingAttachments?.map((a) =>
      a.id === attachmentId ? { ...a, uploadProgress: progress } : a
    );

    // Calculate overall upload progress
    const totalProgress = updatedAttachments
      ? updatedAttachments.reduce((sum, a) => sum + a.uploadProgress, 0) / updatedAttachments.length
      : 100;

    return {
      ...m,
      pendingAttachments: updatedAttachments,
      uploadProgress: totalProgress,
    };
  });
}

/**
 * Remove a message from the queue
 */
export function removeMessage(messages: PendingMessage[], clientId: string): PendingMessage[] {
  return messages.filter((m) => m.clientId !== clientId);
}

/**
 * Add a message to the queue
 */
export function addMessage(messages: PendingMessage[], message: PendingMessage): PendingMessage[] {
  return [...messages, message];
}

/**
 * Get pending messages for a specific chat type and optionally a conversation
 */
export function getMessagesForContext(
  messages: PendingMessage[],
  chatType: ChatType,
  conversationId?: string
): PendingMessage[] {
  return messages.filter((m) => {
    if (m.chatType !== chatType) return false;
    if (chatType === 'dm' && m.conversationId !== conversationId) return false;
    return true;
  });
}

/**
 * Check if a message with the given server ID exists (for deduplication)
 */
export function hasServerMessage(messages: PendingMessage[], serverId: string): boolean {
  return messages.some((m) => m.serverId === serverId);
}

/**
 * Clean up sent messages that have been confirmed by the server
 * This removes messages where we have a serverId and sufficient time has passed
 */
export function cleanupSentMessages(messages: PendingMessage[]): PendingMessage[] {
  const now = Date.now();
  return messages.filter((m) => {
    // Keep if not sent
    if (m.status !== 'sent') return true;
    // Keep if no serverId (shouldn't happen but be safe)
    if (!m.serverId) return true;
    // Remove if cleanup delay has passed
    return now - m.createdAt < MESSAGE_QUEUE_CONFIG.CLEANUP_DELAY_MS;
  });
}
